import { Extension, Editor, Mark, ChainedCommands } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import DiffMatchPatch from 'diff-match-patch'
import { Transaction, EditorState } from '@tiptap/pm/state'
import './ai-changes.scss'
/**
 * A change made by the AI Agent. The changes are computed by making a diff
 * between the old and new document (before and after the AI Agent's changes).
 */
export interface AiChange {
  /**
   * Unique identifier for the change.
   */
  id: string
  /**
   * The range of content that was deleted in the old document.
   */
  oldRange: { from: number; to: number }
  /**
   * The range of content that was added in the new document.
   * This is the range of content that was inserted by the AI Agent.
   */
  newRange: { from: number; to: number }
  /**
   * Type of change (added, removed, modified)
   */
  type: 'added' | 'removed' | 'modified'
  /**
   * Content of the change
   */
  content?: string
}

/**
 * Options for the `getCustomDecorations` function
 */
export interface GetCustomDecorationsOptions {
  /**
   * The current change being processed
   */
  change: AiChange
  /**
   * The list of changes made by the AI
   */
  changes: AiChange[]
  /**
   * Whether the current change is selected
   */
  isSelected: boolean
  /**
   * Get the default decorations that the extension would apply to the document
   * if the `getCustomDecorations` function was not provided.
   */
  getDefaultDecorations: () => Decoration[]
  /**
   * The Tiptap Editor instance
   */
  editor: Editor
  /**
   * The previous document before the AI made changes
   */
  previousDoc: ProseMirrorNode | null
  /**
   * The current document after the AI made changes
   */
  currentDoc: ProseMirrorNode
}

/**
 * Configuration options for the AI Changes extension
 */
export interface AiChangesOptions {
  /**
   * Creates the decorations for an AI Change
   */
  getCustomDecorations: (options: GetCustomDecorationsOptions) => Decoration[]
}

/**
 * Internal state for the AI Changes ProseMirror plugin
 */
export interface AiChangesProsemirrorPluginState {
  previousDoc: ProseMirrorNode | null
  selectionRange: { from: number; to: number } | null
  isTrackingChanges: boolean
  isShowingChanges: boolean
  changes: AiChange[]
  selectedChangeId: string | null
  decorations: DecorationSet
}

/**
 * Internal storage properties for the AI Changes extension.
 */
export interface AiChangesStorage {
  /** Key for the ProseMirror plugin used by the extension */
  pluginKey: PluginKey<AiChangesProsemirrorPluginState>
  /**
   * Returns a list of changes made to the document by the AI
   */
  getChanges: () => AiChange[]
  /**
   * Returns the currently selected change in the document
   */
  getSelectedChange: () => AiChange | null
  /**
   * Returns the previous document before the AI made changes
   */
  getPreviousDoc: () => ProseMirrorNode | null
  /**
   * Returns `true` if the extension is currently tracking changes made by the AI
   */
  getIsTrackingAiChanges: () => boolean
  /**
   * Returns `true` if the extension is currently showing the changes made by the AI
   */
  getIsShowingAiChanges: () => boolean
}

// Plugin key for the AI Changes extension
export const aiChangesPluginKey = new PluginKey<AiChangesProsemirrorPluginState>('aiChanges')

/**
 * Compute differences between two documents
 */
function computeDocumentDiff(oldDoc: ProseMirrorNode, newDoc: ProseMirrorNode, selectionRange?: { from: number, to: number }): AiChange[] {
  console.log('[AIChanges] computeDocumentDiff ENTRY', {
    oldDoc,
    newDoc,
    selectionRange,
    oldDocText: oldDoc.textBetween(0, oldDoc.content.size, ' '),
    newDocText: newDoc.textBetween(0, newDoc.content.size, ' ')
  })
  const changes: AiChange[] = []
  let changeId = 0

  // If we have a selection range, treat it as the old content
  if (selectionRange) {
    const oldText = oldDoc.textBetween(selectionRange.from, selectionRange.to, ' ')
    const newText = newDoc.textBetween(selectionRange.from, selectionRange.to, ' ')
    console.log('[AIChanges] Diffing selection range', { selectionRange, oldText, newText })

    // If the text at selection changed, record both the old and new content
    if (oldText !== newText) {
      // First record the removed content
      if (oldText) {
        changes.push({
          id: `change-${changeId++}`,
          oldRange: { from: selectionRange.from, to: selectionRange.to },
          newRange: { from: selectionRange.from, to: selectionRange.from },
          type: 'removed',
          content: oldText
        })
        console.log('[AIChanges] Detected removed content', oldText)
      }

      // Then record the added content
      if (newText) {
        changes.push({
          id: `change-${changeId++}`,
          oldRange: { from: selectionRange.from, to: selectionRange.from },
          newRange: { from: selectionRange.from, to: selectionRange.from + newText.length },
          type: 'added',
          content: newText
        })
        console.log('[AIChanges] Detected added content', newText)
      }
    }
  }

  // Also scan for any nodes with data-ai-change attribute
  newDoc.descendants((node, pos) => {
    if (node.isText && node.marks.some(mark => mark.type.name === 'aiChange')) {
      changes.push({
        id: `change-${changeId++}`,
        oldRange: { from: pos, to: pos },
        newRange: { from: pos, to: pos + node.nodeSize },
        type: 'added',
        content: node.text || ''
      })
      console.log('[AIChanges] Detected aiChange mark at', pos, node.text)
    }
  })

  console.log('[AIChanges] computeDocumentDiff EXIT, Detected changes:', changes)
  return changes
}

/**
 * Create default decorations for changes
 */
function createDefaultDecorations(
  changes: AiChange[],
  selectedChangeId: string | null,
  doc: ProseMirrorNode
): Decoration[] {
  console.log('[AIChanges] createDefaultDecorations ENTRY', { changes, selectedChangeId, doc })
  const decorations: Decoration[] = []

  for (const change of changes) {
    const isSelected = change.id === selectedChangeId
    let className = ''
    switch (change.type) {
      case 'added':
        className = 'tiptap-ai-changes--new'
        break
      case 'removed':
        className = 'tiptap-ai-changes--old'
        break
      default:
        className = 'tiptap-ai-changes--new'
    }
    if (isSelected) {
      className += ' ai-change--selected'
    }
    if (change.type === 'removed') {
      // No widget, rely on mark for inline ghost
      continue
    } else {
      decorations.push(
        Decoration.inline(
          Math.max(0, change.newRange.from),
          Math.min(doc.content.size, change.newRange.to),
          {
            class: className,
            'data-change-id': change.id,
            'data-change-type': change.type,
          }
        )
      )
      console.log('[AIChanges] createDefaultDecorations: Added decoration', { change, className })
    }
  }
  console.log('[AIChanges] createDefaultDecorations EXIT', decorations)
  return decorations
}

/**
 * AI Changes commands (Tiptap Pro compatible)
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    startTrackingAiChanges: (doc?: ProseMirrorNode) => ReturnType
    stopTrackingAiChanges: () => ReturnType
    setShowAiChanges: (show: boolean) => ReturnType
    acceptAiChange: (id: string) => ReturnType
    rejectAiChange: (id: string) => ReturnType
    selectAiChange: (id: string) => ReturnType
    acceptAllAiChanges: () => ReturnType
    rejectAllAiChanges: () => ReturnType
  }
}

export interface CommandFunctionProps {
  tr: Transaction
  state: EditorState
  dispatch: ((tr: Transaction) => void) | undefined
  chain: () => ChainedCommands
}

export const AiChangesExtension = Extension.create<AiChangesOptions, AiChangesStorage>({
  name: 'aiChanges',

  addOptions() {
    return {
      getCustomDecorations: ({ change, isSelected, getDefaultDecorations }) => {
        const decorations = getDefaultDecorations()
        if (isSelected) {
          decorations.push(
            Decoration.widget(change.newRange.to, () => {
              const element = document.createElement('span')
              element.className = 'ai-change-popover-anchor'
              element.setAttribute('data-change-id', change.id)
              return element
            })
          )
        }
        return decorations
      },
    }
  },

  addStorage() {
    const self = this as unknown as { editor: Editor }
    return {
      pluginKey: aiChangesPluginKey,
      getChanges: () => {
        if (!self.editor?.state) return []
        const pluginState = aiChangesPluginKey.getState(self.editor.state)
        return pluginState?.changes || []
      },
      getSelectedChange: () => {
        if (!self.editor?.state) return null
        const pluginState = aiChangesPluginKey.getState(self.editor.state)
        if (!pluginState?.selectedChangeId) return null
        return pluginState.changes.find(c => c.id === pluginState.selectedChangeId) || null
      },
      getPreviousDoc: () => {
        if (!self.editor?.state) return null
        const pluginState = aiChangesPluginKey.getState(self.editor.state)
        return pluginState?.previousDoc || null
      },
      getIsTrackingAiChanges: () => {
        if (!self.editor?.state) return false
        const pluginState = aiChangesPluginKey.getState(self.editor.state)
        return pluginState?.isTrackingChanges || false
      },
      getIsShowingAiChanges: () => {
        if (!self.editor?.state) return false
        const pluginState = aiChangesPluginKey.getState(self.editor.state)
        return pluginState?.isShowingChanges || false
      },
    }
  },

  addCommands() {
    console.log('AI Changes addCommands registered');
    return {
      startTrackingAiChanges: (doc?: ProseMirrorNode) => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>startTrackingAiChanges command called', { doc, tr, state, dispatch, chain });
        if (dispatch) {
          console.log('>>>startTrackingAiChanges command dispatching');
          const meta = {
            type: 'startTrackingChanges',
            previousDoc: doc || state.doc,
            selectionRange: {
              from: state.selection.from,
              to: state.selection.to
            }
          }
          dispatch(tr.setMeta(aiChangesPluginKey, meta))
          // Also set showing changes to true by default
          // dispatch(tr.setMeta(aiChangesPluginKey, {
          //   type: 'setShowChanges',
          //   show: true,
          // }))
          return true
        }
        return chain()
      },
      stopTrackingAiChanges: () => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>stopTrackingAiChanges command called', { tr, dispatch, chain });
        if (dispatch) {
          dispatch(tr.setMeta(aiChangesPluginKey, { type: 'stopTrackingChanges' }))
          return true
        }
        return chain()
      },
      setShowAiChanges: (show: boolean) => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>setShowAiChanges command called', { tr, dispatch, chain });
        if (dispatch) {
          dispatch(tr.setMeta(aiChangesPluginKey, { type: 'setShowChanges', show }))
          return true
        }
        return chain()
      },
      acceptAiChange: (id: string) => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>acceptAiChange command called', { tr, dispatch, chain, id });
        if (dispatch) {
          const pluginState = aiChangesPluginKey.getState(state)
          const change = pluginState?.changes.find(c => c.id === id)
          if (change) {
            // For accepting a change, we keep the new content as is
            // Just update the plugin state to remove this change
            dispatch(tr.setMeta(aiChangesPluginKey, { type: 'acceptChange', changeId: id }))
          }
          return true
        }
        return chain()
      },
      rejectAiChange: (id: string) => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>rejectAiChange command called', { tr, dispatch, chain, id });
        if (dispatch) {
          const pluginState = aiChangesPluginKey.getState(state)
          const change = pluginState?.changes.find(c => c.id === id)
          if (change) {
            // For rejecting a change, we need to:
            // 1. Delete the new content
            // 2. If there was old content, restore it
            // 3. Update plugin state
            if (change.type === 'added') {
              tr = tr.delete(change.newRange.from, change.newRange.to)
            } else if (change.type === 'removed' && change.content) {
              tr = tr.insertText(change.content, change.newRange.from)
            }
            dispatch(tr.setMeta(aiChangesPluginKey, { type: 'rejectChange', changeId: id }))
          }
          return true
        }
        return chain()
      },
      selectAiChange: (id: string) => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>selectAiChange command called', { tr, dispatch, chain, id });
        if (dispatch) {
          dispatch(tr.setMeta(aiChangesPluginKey, { type: 'selectChange', changeId: id }))
          return true
        }
        return chain()
      },
      acceptAllAiChanges: () => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>acceptAllAiChanges command called', { tr, dispatch, chain });
        if (dispatch) {
          const { doc } = state;
          const rangesToRemove: {from: number, to: number}[] = [];
          
          const deletedMarkType = state.schema.marks.deleted;
          const aiChangeOldMarkType = state.schema.marks.aiChangeOld;
          const aiChangeMarkType = state.schema.marks.aiChange;

          // 1. Remove the 'aiChange' mark from the entire document to accept insertions
          if (aiChangeMarkType) {
            tr.removeMark(0, doc.content.size, aiChangeMarkType);
          }

          // 2. Find all text nodes with 'deleted' or 'aiChangeOld' marks
          doc.descendants((node, pos) => {
            if (node.isText) {
              const hasDeletedMark = deletedMarkType && node.marks.some(mark => mark.type === deletedMarkType);
              const hasOldMark = aiChangeOldMarkType && node.marks.some(mark => mark.type === aiChangeOldMarkType);
              if (hasDeletedMark || hasOldMark) {
                rangesToRemove.push({ from: pos, to: pos + node.nodeSize });
              }
            }
          });
          
          // 3. Remove the 'deleted' ranges in reverse order to avoid position mapping issues
          for (let i = rangesToRemove.length - 1; i >= 0; i--) {
            tr.delete(rangesToRemove[i].from, rangesToRemove[i].to);
          }

          // 4. After document modifications, clear the plugin state
          tr.setMeta(aiChangesPluginKey, { type: 'acceptAllChanges' });
          
          dispatch(tr);
          return true;
        }
        return chain();
      },
      rejectAllAiChanges: () => ({ tr, state, dispatch, chain }: CommandFunctionProps) => {
        console.log('>>>rejectAllAiChanges command called', { tr, dispatch, chain });
        if (dispatch) {
          const { doc } = state;
          const rangesToRemove: {from: number, to: number}[] = [];
          
          const deletedMarkType = state.schema.marks.deleted;
          const aiChangeOldMarkType = state.schema.marks.aiChangeOld;
          const aiChangeMarkType = state.schema.marks.aiChange;

          // 1. Find all text nodes with the 'aiChange' mark to remove them (rejecting insertions)
          if (aiChangeMarkType) {
            doc.descendants((node, pos) => {
              if (node.isText && node.marks.some(mark => mark.type === aiChangeMarkType)) {
                rangesToRemove.push({ from: pos, to: pos + node.nodeSize });
              }
            });
          }

          // 2. Remove the 'deleted' and 'aiChangeOld' marks from the entire document to restore text
          if (deletedMarkType) {
            tr.removeMark(0, doc.content.size, deletedMarkType);
          }
          if (aiChangeOldMarkType) {
            tr.removeMark(0, doc.content.size, aiChangeOldMarkType);
          }
          
          // 3. Remove the 'aiChange' ranges in reverse order
          for (let i = rangesToRemove.length - 1; i >= 0; i--) {
            tr.delete(rangesToRemove[i].from, rangesToRemove[i].to);
          }

          // 4. After document modifications, clear the plugin state
          tr.setMeta(aiChangesPluginKey, { type: 'rejectAllChanges' });
          
          dispatch(tr);
          return true;
        }
        return chain();
      },
    }
  },

  addProseMirrorPlugins() {
    const options = this.options
    const editor = this.editor
    
    return [
      new Plugin<AiChangesProsemirrorPluginState>({
        key: aiChangesPluginKey,
        state: {
          init() {
            const initialState = {
              previousDoc: null,
              selectionRange: null,
              isTrackingChanges: false,
              isShowingChanges: false,
              changes: [],
              selectedChangeId: null,
              decorations: DecorationSet.empty
            }
            console.log('[AIChanges] plugin init', initialState)
            return initialState
          },
          apply(tr, pluginState, _oldState, newState) {
            const meta = tr.getMeta(aiChangesPluginKey)
            console.log('[AIChanges] plugin apply', { meta, pluginStateBefore: pluginState, tr })
            let newPluginState = { ...pluginState }

            // Log tracking mode
            if (pluginState.isTrackingChanges) {
              console.log('[AIChanges] plugin is in TRACKING mode', {
                meta
              })
            } else {
              console.log('[AIChanges] plugin is NOT tracking', {
                meta
              })
            }

            // Handle plugin state updates based on meta
            if (meta?.type === 'startTrackingChanges') {
              newPluginState = {
                ...newPluginState,
                previousDoc: meta.previousDoc,
                selectionRange: meta.selectionRange,
                isTrackingChanges: true,
                isShowingChanges: true,
                changes: [],
                selectedChangeId: null,
              }
              console.log('[AIChanges] startTrackingChanges', newPluginState)
            } else if (meta?.type === 'stopTrackingChanges') {
              newPluginState = {
                ...newPluginState,
                previousDoc: null,
                selectionRange: null,
                isTrackingChanges: false,
                isShowingChanges: false,
                changes: [],
                selectedChangeId: null,
              }
              console.log('[AIChanges] stopTrackingChanges', newPluginState)
            } else if (meta?.type === 'setShowChanges') {
              newPluginState = {
                ...newPluginState,
                isShowingChanges: meta.show,
              }
              console.log('[AIChanges] setShowChanges', newPluginState)
            } else if (meta?.type === 'acceptChange') {
              newPluginState = {
                ...newPluginState,
                changes: newPluginState.changes.filter(c => c.id !== meta.changeId),
                selectedChangeId: null,
              }
              console.log('[AIChanges] acceptChange', newPluginState)
            } else if (meta?.type === 'rejectChange') {
              newPluginState = {
                ...newPluginState,
                changes: newPluginState.changes.filter(c => c.id !== meta.changeId),
                selectedChangeId: null,
              }
              console.log('[AIChanges] rejectChange', newPluginState)
            } else if (meta?.type === 'selectChange') {
              newPluginState = {
                ...newPluginState,
                selectedChangeId: meta.changeId,
              }
              console.log('[AIChanges] selectChange', newPluginState)
            } else if (meta?.type === 'acceptAllChanges') {
              newPluginState = {
                ...newPluginState,
                previousDoc: null,
                selectionRange: null,
                isTrackingChanges: false,
                isShowingChanges: false,
                changes: [],
                selectedChangeId: null,
              }
              console.log('[AIChanges] acceptAllChanges', newPluginState)
            } else if (meta?.type === 'rejectAllChanges') {
              newPluginState = {
                ...newPluginState,
                previousDoc: null,
                selectionRange: null,
                isTrackingChanges: false,
                isShowingChanges: false,
                changes: [],
                selectedChangeId: null,
              }
              console.log('[AIChanges] rejectAllChanges', newPluginState)
            }

            // Always run the diff if updateChanges meta is present and previousDoc exists
            if ((meta?.type === 'updateChanges' && newPluginState.previousDoc) ||
                (newPluginState.isTrackingChanges && tr.docChanged && newPluginState.previousDoc)) {
              // Compute changes using selection-based detection
              const changes = computeDocumentDiff(
                newPluginState.previousDoc, 
                newState.doc,
                newPluginState.selectionRange || undefined
              )
              console.log('[AIChanges] Detected AI changes:', changes)
              newPluginState = {
                ...newPluginState,
                changes
              }
            }

            // Update decorations if showing changes
            console.log('[AIChanges] plugin apply decorations', { newPluginState, meta })
            if (newPluginState.isShowingChanges && newPluginState.changes.length > 0) {
              const decorations = newPluginState.changes.flatMap(change => {
                const isSelected = change.id === newPluginState.selectedChangeId
                return options.getCustomDecorations({
                  change,
                  changes: newPluginState.changes,
                  isSelected,
                  getDefaultDecorations: () => createDefaultDecorations([change], isSelected ? change.id : null, newState.doc),
                  editor,
                  previousDoc: newPluginState.previousDoc,
                  currentDoc: newState.doc
                })
              })
              console.log('[AIChanges] Setting decorations:', decorations)
              newPluginState = {
                ...newPluginState,
                decorations: DecorationSet.create(newState.doc, decorations)
              }
            } else {
              // Clear decorations if not showing changes or no changes
              if (newPluginState.decorations !== DecorationSet.empty) {
                console.log('[AIChanges] Clearing decorations')
                newPluginState = {
                  ...newPluginState,
                  decorations: DecorationSet.empty
                }
              }
            }
            console.log('[AIChanges] plugin apply END', { pluginStateAfter: newPluginState })
            return newPluginState
          }
        },
        props: {
          decorations(state) {
            const deco = this.getState(state)?.decorations
            // console.log('[AIChanges] decorations prop', deco)
            return deco
          },
          handleClick(view, pos, event) {
            // Find change at click position
            const pluginState = this.getState(view.state)
            if (!pluginState?.changes.length) return false
            const changeId = pluginState.changes.find(change => 
              pos >= change.newRange.from && pos <= change.newRange.to
            )?.id
            if (changeId) {
              view.dispatch(view.state.tr.setMeta(aiChangesPluginKey, {
                type: 'selectChange',
                changeId
              }))
              return true
            }
            return false
          }
        }
      })
    ]
  },
})

// AI Change Mark
export const AiChangeMark = Mark.create({
  name: 'aiChange',
  addOptions() { return {} },
  parseHTML() { return [{ tag: 'span[data-ai-change]' }] },
  renderHTML() { return ['span', { 'data-ai-change': 'true', class: 'tiptap-ai-changes--new' }, 0] },
})

// Deleted mark for inline ghost deletions
export const DeletedMark = Mark.create({
  name: 'deleted',
  parseHTML() { return [{ tag: 'span.tiptap-ai-changes--old' }] },
  renderHTML() { return ['span', { class: 'tiptap-ai-changes--old' }, 0] },
})

// Add the mark for old text that was replaced
export const AiChangeOldMark = Mark.create({
  name: 'aiChangeOld',
  
  addOptions() { return {} },
  
  parseHTML() { return [{ tag: 'span.tiptap-ai-changes--old' }] },
  
  renderHTML() { return ['span', { class: 'tiptap-ai-changes--old' }, 0] }
})

export default AiChangesExtension 